import { VisitMethod } from './visits';

export { VisitMethod };

export interface Availabilities {
  availabilities: ProviderAvailability[];
  selectedProvider?: Provider;
  availabilityDate: string;
  isSoonerProviderAvailable?: boolean;
}

export interface ConsentForm {
  name: string;
  url: string;
  individualConsentRequired: boolean;
}

export interface FraudData {
  toDateTime: string;
  remainingAttemptCount: number;
}

export interface FraudTracking {
  addCreditCardInvalidAttemptSummary?: FraudData;
}

export interface PatientAccount {
  address?: PatientAddress;
  birthDate?: string;
  email?: string;
  firstName?: string;
  gender?: PatientGender | '';
  lastName?: string;
  medicalRecordNumber?: string;
  patientId?: string;
  phoneNumber?: PatientPhone;
}

export interface PatientAddress {
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  stateCode: string;
  zip: string;
  countryCode: string;
}

export interface PatientCreditCard {
  id: string;
  number: string;
  type: string;
  expiry: string;
  isDefault: boolean;
}

export interface PatientPhone {
  countryCode: string;
  number: string;
  prefix: string;
}

export enum PatientGender {
  FEMALE = 'female',
  MALE = 'male',
}

export interface Payment {
  price: number;
  cancellationFee: number;
  visitCreditAmount: number;
  transactionAmount: number;
  creditCard: {
    number: string;
    type: string;
  };
}

export enum PhoneVerificationChannels {
  SMS = 'sms',
  CALL = 'call',
}

export interface Provider {
  firstName: string;
  id: string;
  imageUrl: string;
  lastName: string;
  prefix?: string;
  suffix?: string;
}

export interface ProviderAvailability {
  provider: Provider;
  timeSlotCollection: TimeSlot[];
  lastEndedVisitDateTime?: string;
}

export interface RebookingAvailabilities {
  availableProviders: ProviderAvailability[];
  futureAvailableProviders: ProviderAvailability[];
}

export type RescheduledReservedVisit = Pick<
  ReservedVisit,
  'reservationId' | 'expireDateTime' | 'allowedMethods'
>;

export type RescheduledOrNewReservedVisit =
  | RescheduledReservedVisit
  | ReservedVisit;

export interface ReservedVisit {
  allowedMethods: VisitMethod[];
  consentForms: ConsentForm[];
  isFollowupVisitIncluded: boolean;
  isReferralAllowed: boolean;
  expireDateTime: string;
  price: number;
  reservationId: string;
  subscriptionBasedPrice: number | null;
  visitCreditAmount: number;
}

export interface SelectedVisit {
  date: string;
  provider: Provider;
  time: string;
  timeZone: string;
  visitTypeId: string;
  visitId?: string | null;
}

export interface ServiceType {
  abbreviation: string;
  category: string;
  serviceTypeId: string;
  name: string;
  shortDescription: string | null;
  description: string;
  imageUrl: string | null;
  visitTypes: VisitType[];
  hasPastVisit?: boolean;
  isDrugScreenRequired?: boolean;
  isInPersonVisitRequired?: boolean;
}

export interface VisitType {
  description: string;
  isDrugScreen: boolean;
  isDrugScreenRequired?: boolean;
  isInPerson?: boolean;
  isReferralAllowed: boolean;
  name: string;
  price: number;
  referralCreditAmount: number;
  visitTypeId: string;
}

export interface TimeSlot {
  date: string;
  times: string[];
}

export interface UtmParameters {
  gclid?: string | null;
  campaign?: string | null;
  content?: string | null;
  fbclid?: string | null;
  fbpixel?: string | null;
  fbp?: string | null;
  medium?: string | null;
  queryString?: string | null;
  referrer?: string | null;
  source?: string | null;
  term?: string | null;
  service?: string | null;
  referralCode?: string | null;
}

export interface LegacyVerifiedOptions {
  isActive: boolean;
  states: Array<string>;
}

export interface VisitEvent {
  type: string;
  dateTime: string;
}

export interface Video {
  videoCallId: string;
  roomName: string;
  accessToken: string;
}

export interface AvailableSubscription {
  subscriptionPlanGlobalKey: string;
  name: string;
  price: number;
  period: string;
  hasFreeTrial: boolean;
}
